/**
 * PDP Hero — Swiper init for the variant image gallery.
 * Depends on Swiper v11 loaded via CDN.
 *
 * Design: scripts are deferred, so this file executes after the DOM is ready
 * and after swiper-bundle.min.js (which is loaded with defer, earlier in source).
 * We still guard on `typeof Swiper` in case CDN fails.
 */
(function () {
  'use strict';

  function initGalleries() {
    if (typeof Swiper === 'undefined') {
      // Swiper failed to load (CDN blocked, offline, etc) — bail gracefully;
      // the gallery still renders as a vertical stack.
      console.warn('[pdp-hero] Swiper not loaded — gallery will render as static stack.');
      return;
    }

    document.querySelectorAll('[data-pdp-hero]').forEach(function (hero) {
      const mainEl = hero.querySelector('[data-pdp-gallery-main]');
      const thumbsEl = hero.querySelector('[data-pdp-gallery-thumbs]');
      const prevBtn = hero.querySelector('[data-pdp-gallery-prev]');
      const nextBtn = hero.querySelector('[data-pdp-gallery-next]');

      if (!mainEl) return;

      let thumbsSwiper = null;
      if (thumbsEl) {
        thumbsSwiper = new Swiper(thumbsEl, {
          slidesPerView: 5,
          spaceBetween: 8,
          watchSlidesProgress: true,
          slideToClickedSlide: true,
          freeMode: { enabled: true, sticky: true },
          breakpoints: {
            750: { slidesPerView: 6, spaceBetween: 10 }
          }
        });
      }

      new Swiper(mainEl, {
        slidesPerView: 1,
        spaceBetween: 0,
        speed: 300,
        navigation: {
          prevEl: prevBtn,
          nextEl: nextBtn
        },
        thumbs: thumbsSwiper ? { swiper: thumbsSwiper } : undefined,
        keyboard: { enabled: true },
        a11y: {
          prevSlideMessage: 'Previous product image',
          nextSlideMessage: 'Next product image'
        }
      });
    });
  }

  function initTestimonials() {
    document.querySelectorAll('[data-pdp-testimonial]').forEach(function (container) {
      const cards = Array.from(container.querySelectorAll('[data-pdp-testimonial-card]'));
      const dots = Array.from(container.querySelectorAll('[data-pdp-testimonial-dot]'));
      const prev = container.querySelector('[data-pdp-testimonial-prev]');
      const next = container.querySelector('[data-pdp-testimonial-next]');
      if (cards.length < 2) {
        // Hide nav + dots when only 1 testimonial (can't rotate through one).
        if (prev) prev.hidden = true;
        if (next) next.hidden = true;
        return;
      }

      let idx = 0;

      function activate(newIdx) {
        idx = (newIdx + cards.length) % cards.length;
        cards.forEach(function (c, i) {
          const isActive = i === idx;
          c.classList.toggle('is-active', isActive);
          if (isActive) {
            c.removeAttribute('aria-hidden');
          } else {
            c.setAttribute('aria-hidden', 'true');
          }
        });
        dots.forEach(function (d, i) {
          d.classList.toggle('is-active', i === idx);
        });
      }

      if (prev) prev.addEventListener('click', function () { activate(idx - 1); });
      if (next) next.addEventListener('click', function () { activate(idx + 1); });
      dots.forEach(function (d, i) {
        d.addEventListener('click', function () { activate(i); });
      });

      // Auto-advance every 7s, pause on hover/focus for accessibility.
      let timer = setInterval(function () { activate(idx + 1); }, 7000);
      container.addEventListener('mouseenter', function () { clearInterval(timer); });
      container.addEventListener('focusin', function () { clearInterval(timer); });
      container.addEventListener('mouseleave', function () {
        timer = setInterval(function () { activate(idx + 1); }, 7000);
      });
    });
  }

  /**
   * Open the UpCart slide drawer. UpCart exposes a global `window.upcartOpenCart`
   * function when it's initialized. If UpCart isn't on the page (e.g. store dev
   * preview without the app mounted), we fall back to dispatching a generic
   * `cart:refresh` event and, as a last resort, navigating to /cart.
   */
  function openUpCart() {
    if (typeof window.upcartOpenCart === 'function') {
      window.upcartOpenCart();
      return;
    }
    // Some UpCart builds expose window.AirCart or a global event hook.
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('upcart:open', { bubbles: true }));
    // If UpCart really isn't on the page, the user still gets feedback via the
    // cart count bubble updating on next nav. Don't hard-redirect — would yank
    // the shopper out of the PDP.
    console.warn('[pdp-hero] UpCart drawer not available — dispatched cart:refresh fallback.');
  }

  function initBuyBox() {
    document.querySelectorAll('[data-pdp-buybox]').forEach(function (form) {
      const sellingPlanInput = form.querySelector('[data-pdp-buybox-selling-plan]');
      const radios = form.querySelectorAll('[data-pdp-buybox-radio]');
      const expandBtn = form.querySelector('[data-pdp-buybox-expand]');
      const benefitsList = form.querySelector('.pdp-buybox__benefits');
      const submitBtn = form.querySelector('[data-atc-button]');
      const submitText = form.querySelector('.pdp-buybox__submit-text');
      const errorSlot = form.querySelector('[data-pdp-buybox-error]');

      // Wire radio → hidden selling_plan input. One-time = "", Subscribe = plan gid.
      // Also repaint the hero price block (price / compare-at / SAVE pill) from
      // data-* on the chosen radio. Liquid stamps pre-formatted money strings on
      // each radio at render time so JS never has to do currency math.
      const priceEl = document.querySelector('[data-pdp-price]');
      const compareEl = document.querySelector('[data-pdp-compare-at]');
      const saveEl = document.querySelector('[data-pdp-save-pill]');
      const subBadgeEl = document.querySelector('[data-pdp-sub-badge]');
      // Campaign sale badge ("Mother's Day Sale · Save X%") — repaint the % on
      // selling-plan toggle so it always agrees with the price-row SAVE pill.
      const campaignSaveEl = document.querySelector('.campaign-sale-badge__save');

      function applyPriceFromRadio(radio) {
        if (!radio) return;
        const price = radio.getAttribute('data-price') || '';
        const compare = radio.getAttribute('data-compare-at') || '';
        const savePct = radio.getAttribute('data-save-pct') || '';
        const isSubscribe = radio.value === 'subscribe';

        if (priceEl && price) priceEl.textContent = price;

        if (compareEl) {
          if (compare) {
            compareEl.textContent = compare;
            compareEl.hidden = false;
          } else {
            compareEl.textContent = '';
            compareEl.hidden = true;
          }
        }

        if (saveEl) {
          if (savePct) {
            saveEl.textContent = 'SAVE ' + savePct + '%';
            saveEl.hidden = false;
          } else {
            saveEl.textContent = '';
            saveEl.hidden = true;
          }
        }

        // Campaign sale badge mirrors the SAVE pill % so the two adjacent
        // numbers can never disagree (e.g. SAVE 28% / Mother's Day Sale Save 15%).
        if (campaignSaveEl && savePct) {
          campaignSaveEl.textContent = 'Save ' + savePct + '%';
        }

        // Subscription price badge — only shown in the subscribe state.
        if (subBadgeEl) {
          subBadgeEl.hidden = !isSubscribe;
        }
      }

      // Keep the shopper's Subscribe / One-time choice across a size switch.
      // The variant swap re-renders this form with the Liquid default checked,
      // which silently dropped a subscriber back to one-time (and, now that
      // the frequency picker remembers its value, would show "Every 3 Months"
      // beside a one-time purchase). Only an explicit shopper choice is
      // remembered, so the render-time default still governs a fresh page.
      const rememberedOption = window.__tcPdpPurchaseOption;
      if (rememberedOption) {
        const rememberedRadio = form.querySelector('[data-pdp-buybox-radio][value="' + rememberedOption + '"]');
        if (rememberedRadio) rememberedRadio.checked = true;
      }

      radios.forEach(function (radio) {
        radio.addEventListener('change', function () {
          if (!radio.checked) return;
          window.__tcPdpPurchaseOption = radio.value;
          if (sellingPlanInput) sellingPlanInput.value = radio.getAttribute('data-selling-plan') || '';
          applyPriceFromRadio(radio);
        });
      });

      // Delivery-frequency picker (every 1 / 2 / 3 months). The subscribe
      // radio's data-selling-plan is the single source the radio handler and
      // the initial sync both read, so the picker just rewrites that attribute
      // — and the hidden input when Subscribe is the active choice. Picking a
      // frequency while One-time is selected switches the shopper to Subscribe:
      // nobody chooses a delivery schedule for a one-off purchase.
      // The choice survives a variant swap (the section re-renders and this
      // init re-runs) via a page-level variable, applied only when the new
      // variant actually offers that plan.
      const freqSelect = form.querySelector('[data-pdp-buybox-frequency]');
      const subscribeRadio = form.querySelector('[data-pdp-buybox-radio][value="subscribe"]');
      if (freqSelect && subscribeRadio) {
        const remembered = window.__tcPdpFrequency;
        if (remembered && freqSelect.querySelector('option[value="' + remembered + '"]')) {
          freqSelect.value = remembered;
        }
        const applyFrequency = function () {
          subscribeRadio.setAttribute('data-selling-plan', freqSelect.value);
          if (subscribeRadio.checked && sellingPlanInput) sellingPlanInput.value = freqSelect.value;
        };
        applyFrequency();
        freqSelect.addEventListener('change', function () {
          window.__tcPdpFrequency = freqSelect.value;
          applyFrequency();
          if (!subscribeRadio.checked) {
            subscribeRadio.checked = true;
            subscribeRadio.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }

      // Sync initial state to whichever radio is checked at render time. The
      // default may be Subscribe (e.g. during Subscription Savings Week the
      // Liquid stamps `checked` on the subscribe radio). Without this, the
      // price block would keep showing the Liquid-rendered one-time state and
      // the hidden selling_plan input would only get set on a manual toggle.
      // No-op when one-time is the default (its data-* already match the
      // Liquid-rendered price), so this is safe on every PDP + the sampler.
      const initialRadio = form.querySelector('[data-pdp-buybox-radio]:checked');
      if (initialRadio) {
        if (sellingPlanInput) sellingPlanInput.value = initialRadio.getAttribute('data-selling-plan') || '';
        applyPriceFromRadio(initialRadio);
      }

      // Expand/collapse subscribe benefits. stopPropagation keeps the toggle from
      // reaching the card's click-to-select handler below.
      if (expandBtn && benefitsList) {
        expandBtn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          const isExpanded = expandBtn.getAttribute('aria-expanded') === 'true';
          expandBtn.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');
          benefitsList.hidden = isExpanded;
        });
      }

      // Each option card is a <div> with a <label> around just the radio +
      // title (so the benefits toggle and frequency picker aren't read as part
      // of the radio's name). Keep the whole card clickable as it was when the
      // card itself was the <label>: a click on any non-interactive part of the
      // card selects its radio. Clicks on the label, radio, select, buttons and
      // links are left to their native behaviour.
      form.querySelectorAll('[data-pdp-buybox-option]').forEach(function (card) {
        card.addEventListener('click', function (e) {
          if (e.target.closest('label, input, select, button, a, textarea')) return;
          const radio = card.querySelector('[data-pdp-buybox-radio]');
          if (!radio || radio.checked || radio.disabled) return;
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });

      // Intercept form submit → fetch POST /cart/add.js → open UpCart drawer.
      // Teeccino uses UpCart (slide cart app) instead of Shopify's default cart
      // page. The traditional <form action="/cart/add"> submit would full-page
      // reload to /cart (ugly). AJAX submit keeps the shopper on the PDP and
      // triggers UpCart's drawer to slide in.
      const originalText = submitText ? submitText.textContent : 'ADD TO CART';

      form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (!submitBtn || submitBtn.disabled) return;

        // Clear any previous error, enter pending state.
        if (errorSlot) {
          errorSlot.hidden = true;
          errorSlot.textContent = '';
        }
        submitBtn.disabled = true;
        submitBtn.setAttribute('aria-busy', 'true');
        if (submitText) submitText.textContent = 'ADDING…';

        const formData = new FormData(form);
        // Shopify's /cart/add.js wants application/x-www-form-urlencoded or FormData.
        // Empty selling_plan is legal (= one-time). Don't strip it.

        fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
                ? window.Shopify.routes.root + 'cart/add.js'
                : '/cart/add.js', {
          method: 'POST',
          headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
          credentials: 'same-origin'
        })
          .then(function (res) {
            return res.json().then(function (body) {
              return { ok: res.ok, status: res.status, body: body };
            });
          })
          .then(function (result) {
            if (!result.ok) {
              const message = (result.body && (result.body.description || result.body.message)) ||
                'Could not add to cart — please try again.';
              if (errorSlot) {
                errorSlot.textContent = message;
                errorSlot.hidden = false;
              }
              return;
            }
            // Success — defer openUpCart + publish through a cancellable
            // 'pdp:atc-success' CustomEvent so other modules (donation upsell,
            // etc.) can intercept and slot a modal BETWEEN the cart add and
            // the drawer opening. A listener takes ownership by calling
            // e.preventDefault() and is responsible for calling
            // e.detail.resume() when it's done. If nothing cancels, we call
            // resume() ourselves and the flow runs exactly as before.
            function resume() {
              openUpCart();
              if (window.PUB_SUB_EVENTS && window.publish) {
                window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'pdp-hero', productVariantId: formData.get('id') });
              }
            }
            const upsellEvent = new CustomEvent('pdp:atc-success', {
              cancelable: true,
              bubbles: true,
              detail: {
                addedVariantId: formData.get('id'),
                cartResponse: result.body,
                atcButton: submitBtn,
                resume: resume
              }
            });
            const notPrevented = form.dispatchEvent(upsellEvent);
            if (notPrevented) {
              resume();
            }
          })
          .catch(function (err) {
            console.error('[pdp-hero] ATC failed:', err);
            if (errorSlot) {
              errorSlot.textContent = 'Network error — please try again.';
              errorSlot.hidden = false;
            }
          })
          .finally(function () {
            submitBtn.disabled = false;
            submitBtn.removeAttribute('aria-busy');
            if (submitText) submitText.textContent = originalText;
          });
      });
    });
  }

  /**
   * Variant switching — intercepts clicks on the variant tile radio grid, fetches
   * a fresh render of the hero section via Shopify's Section Rendering API
   * (`?section_id=<id>&variant=<id>`), swaps the hero content in-place, and
   * re-initializes components. Updates history so the URL's ?variant= stays
   * fresh (for share/copy) without a full page reload.
   *
   * Why this approach (Section Rendering) vs client-side patching:
   *   - Server stays the source of truth for price / SAVE % / compare-at / caffeine /
   *     variant images (we don't have to duplicate variant data in JSON on the page).
   *   - Respects any currency conversion, discount apps, or money filter settings
   *     that live server-side.
   *   - Matches Dawn's standard pattern — future theme updates stay compatible.
   *
   * Selected event on the pub/sub bus so cross-sell / buy box / other sections can
   * react if needed.
   */
  /**
   * Delegated tile-click handler, bound ONCE on `document` at script-parse
   * time rather than per-hero at DOMContentLoaded.
   *
   * WHY document-level and why early:
   * Variant tiles are server-rendered `<a href="?variant=...">` so they
   * degrade gracefully with JS off — a click just navigates. That fallback
   * is deliberate, but it also means every millisecond between the tile
   * appearing in the DOM and this handler binding is a window in which a
   * shopper click causes a FULL PAGE RELOAD instead of the instant swap.
   *
   * Measured on live /products/fall-box?view=pdp-bundle (fast desktop,
   * 2026-09-12): tiles present in the HTML at first byte (~29ms),
   * pdp-hero.js executed at ~196ms, DOMContentLoaded at ~460ms. Binding at
   * DOMContentLoaded left a ~260ms dead zone on a FAST connection; on mobile
   * DOMContentLoaded is gated by every other deferred script on the PDP, so
   * the real window is much wider. Binding at parse time closes all of it
   * except the script's own download.
   *
   * Resolving the hero from the tile (rather than closing over it) is what
   * makes document-level binding safe: the handler is inert unless the click
   * lands inside BOTH a [data-pdp-variant-tile] and a [data-pdp-hero], so
   * behaviour is identical to the old per-hero listener, including on pages
   * that render more than one hero.
   */
  function onTileClick(e) {
    const tile = e.target.closest('[data-pdp-variant-tile]');
    if (!tile) return;

    const hero = tile.closest('[data-pdp-hero]');
    if (!hero) return;

    const sectionId = hero.getAttribute('data-section-id');
    const productUrl = hero.getAttribute('data-product-url');
    if (!sectionId || !productUrl) return;

    const newVariantId = tile.getAttribute('data-variant-id');
    if (!newVariantId) return;

    // If user re-clicks the already-selected tile, do nothing.
    if (hero.getAttribute('data-variant-id') === newVariantId) {
      e.preventDefault();
      return;
    }

    // Sold-out tiles are not disabled on the server, but we let the click
    // through — shopper can still see the "sold out" state server-rendered.
    e.preventDefault();
    swapVariant(hero, sectionId, productUrl, newVariantId, tile);
  }

  // Bind immediately, guarded against a duplicate <script src> executing this
  // IIFE twice (which would swap the variant twice per click).
  if (!window.__pdpHeroTileClickBound) {
    window.__pdpHeroTileClickBound = true;
    document.addEventListener('click', onTileClick);
  }

  function initVariantSwitching() {
    document.querySelectorAll('[data-pdp-hero]').forEach(function (hero) {
      const sectionId = hero.getAttribute('data-section-id');
      const productUrl = hero.getAttribute('data-product-url');
      if (!sectionId || !productUrl) return;

      // Click is handled by the document-level listener bound above — see
      // onTileClick(). Only keyboard arrow-key roving focus is bound per-hero.

      // Keyboard support for role="radio" tiles — ArrowLeft/Right/Up/Down move
      // focus to neighbor tiles; Space/Enter activates (the <button> element
      // handles activation natively via click). Per WAI-ARIA radiogroup
      // pattern, focus movement also updates roving tabindex so only the
      // currently-focused tile is reachable via Tab.
      hero.addEventListener('keydown', function (e) {
        const tile = e.target.closest('[data-pdp-variant-tile]');
        if (!tile) return;
        if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].indexOf(e.key) === -1) return;

        e.preventDefault();
        const tiles = Array.from(hero.querySelectorAll('[data-pdp-variant-tile]'));
        const idx = tiles.indexOf(tile);
        if (idx === -1) return;
        const next = (e.key === 'ArrowLeft' || e.key === 'ArrowUp')
          ? tiles[(idx - 1 + tiles.length) % tiles.length]
          : tiles[(idx + 1) % tiles.length];
        if (!next) return;

        // Update roving tabindex: focused tile becomes the single tab stop
        // until selection changes (which is then re-anchored by the server-
        // rendered swap response).
        tiles.forEach(function (t) { t.setAttribute('tabindex', '-1'); });
        next.setAttribute('tabindex', '0');
        next.focus();
      });
    });
  }

  function swapVariant(hero, sectionId, productUrl, newVariantId, triggeredByTile) {
    hero.setAttribute('aria-busy', 'true');
    if (triggeredByTile) triggeredByTile.classList.add('is-loading');

    // Carry forward current URL params (preview_theme_id, _ab, _fd, _sc, locale, etc).
    // Without preview_theme_id, Shopify's primary-domain redirect would bounce
    // the fetch to the live theme and we'd get a page that doesn't have our
    // [data-pdp-hero] structure, causing the swap to fail.
    const currentParams = new URLSearchParams(window.location.search);
    currentParams.set('variant', newVariantId);
    currentParams.set('section_id', sectionId);
    const fetchUrl = productUrl + '?' + currentParams.toString();

    fetch(fetchUrl, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('Section fetch failed: ' + res.status);
        return res.text();
      })
      .then(function (html) {
        // The Section Rendering API returns the section's OUTER HTML (the root
        // element rendered by the section). Parse it, grab the same section's
        // inner content, and swap it into the current DOM.
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        // The returned document wraps the section content; look for the same
        // data-pdp-hero marker to be resilient to wrapper markup.
        const newHero = doc.querySelector('[data-pdp-hero]') || doc.body.firstElementChild;
        if (!newHero) throw new Error('No hero in section response');

        // Replace the hero's inner HTML wholesale. Attributes on the outer
        // <section> (data-variant-id, color-scheme class) also need updating.
        hero.innerHTML = newHero.innerHTML;

        // Sync root-level data/class attributes so the new variant id "sticks".
        const newId = newHero.getAttribute('data-variant-id');
        if (newId) hero.setAttribute('data-variant-id', newId);

        // Update URL so share/copy reflects the new variant.
        if (window.history && history.replaceState) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', newVariantId);
          history.replaceState({}, '', url.toString());
        }

        // Re-init components that live inside the hero (gallery, testimonials, buy box).
        initGalleries();
        initTestimonials();
        initBuyBox();

        // The "Also available as" format switcher binds via document-level
        // delegation, so its tabs keep working across this innerHTML swap with
        // no re-init needed. Only its *selected tab* needs restoring, since the
        // server always renders the default tab active.
        if (window.PDPFormatSwitcher) window.PDPFormatSwitcher.restore();

        // Publish on the theme's pub/sub if available, so buy box / cross-sell /
        // any other subscribers can react.
        if (window.PUB_SUB_EVENTS && window.publish) {
          window.publish(window.PUB_SUB_EVENTS.variantChange, {
            source: 'pdp-hero',
            variantId: newVariantId,
            sectionId: sectionId
          });
        }

        // Return focus to the newly selected tile (accessibility — keeps keyboard
        // users anchored to the control they just activated).
        const newlySelected = hero.querySelector('[data-pdp-variant-tile].is-selected');
        if (newlySelected) newlySelected.focus();
      })
      .catch(function (err) {
        console.error('[pdp-hero] Variant swap failed — falling back to full reload.', err);
        // Fallback: navigate to the tile's stored URL. Tiles used to be <a>
        // elements with href; they're now <button> with data-variant-url so
        // ARIA radio role is unambiguous (S1 of 2026-04-30 a11y fix).
        if (triggeredByTile) {
          var fallbackUrl = triggeredByTile.getAttribute('data-variant-url') || triggeredByTile.href;
          if (fallbackUrl) window.location.href = fallbackUrl;
        }
      })
      .finally(function () {
        hero.removeAttribute('aria-busy');
        if (triggeredByTile) triggeredByTile.classList.remove('is-loading');
      });
  }

  /**
   * Appends a small inline "Nutrition Facts & Ingredients →" link right after
   * the last word of the product description's final <p>. Keeps it on the same
   * line as the closing tagline ("Dark Roast - Organic"), no new line break.
   *
   * Open path: dispatches a 'pdp-nutrition:open' CustomEvent on the dialog.
   * pdp-nutrition.js listens for that event, tracks this link as the opener
   * (so focus returns here on close), and calls dialog.showModal().
   *
   * Previously this clicked a visible trigger button rendered by the separate
   * pdp-nutrition section. That section was dropped from the template on
   * 2026-04-17 (read as orphaned between Bold Flavors and How-to-Brew), so the
   * dialog markup now lives inline in main-pdp-hero.liquid and is opened only
   * via this link.
   *
   * Runs after DOMContentLoaded — the description block is server-rendered
   * in Liquid so we can safely read its paragraphs here.
   */
  function initDescriptionLink() {
    const desc = document.querySelector('[data-pdp-description]');
    if (!desc) return;
    const paras = desc.querySelectorAll('p');
    if (!paras.length) return;
    const lastPara = paras[paras.length - 1];

    // Only wire the inline link if a nutrition dialog exists FOR THIS PRODUCT.
    //
    // This used to be a page-wide first-match lookup, which was fine while the
    // only dialog on a page was the page product's. It stopped being fine once
    // a page could carry several: the sampler landing page renders one per
    // blend. The sampler hero then injected "Nutrition Facts & Ingredients"
    // into the SAMPLER's description pointing at whichever dialog happened to
    // be first in the DOM — a single blend's nutrition label, presented as if
    // it were the whole pack's.
    //
    // Scoping to the hero's own product id restores the intended behaviour: a
    // sampler has no nutrition dialog of its own, because one label cannot
    // represent a multi-flavour box, so no link is injected. Strictly
    // narrowing — on a standard PDP the dialog's uid IS product.id, so the
    // same dialog is found as before.
    const heroEl = document.querySelector('[data-pdp-hero]');
    const heroProductId = heroEl && heroEl.getAttribute('data-product-id');
    const dialog = heroProductId
      ? document.getElementById('pdp-nutrition-modal-' + heroProductId)
      : null;
    if (!dialog) return;

    // Already injected? Re-running after a variant swap must not duplicate.
    if (lastPara.querySelector('[data-pdp-description-link]')) return;

    const link = document.createElement('button');
    link.type = 'button';
    link.className = 'pdp-hero__description-link';
    link.setAttribute('data-pdp-description-link', '');
    link.setAttribute('aria-haspopup', 'dialog');
    link.setAttribute('aria-controls', dialog.id || 'pdp-nutrition-modal');
    // Note (2026-04-30 a11y S2 fix): no aria-label. The visible text
    // "Nutrition Facts & Ingredients" IS the accessible name (WCAG 2.5.3
    // Label in Name). Earlier `aria-label="View nutrition facts and
    // ingredients"` substituted "and" for "&" — voice-control users saying
    // "click Nutrition Facts &" failed to match. aria-haspopup +
    // aria-controls already convey the modal-trigger behavior.
    // Leading non-breaking space so it never orphans onto its own line
    // alone — stays glued to the preceding word ("Organic").
    link.innerHTML = '\u00A0Nutrition Facts &amp; Ingredients&nbsp;→';

    link.addEventListener('click', function (e) {
      e.preventDefault();
      const targetDialog = document.querySelector('[data-pdp-nutrition-dialog]');
      if (!targetDialog) return;
      targetDialog.dispatchEvent(new CustomEvent('pdp-nutrition:open', {
        detail: { opener: link }
      }));
    });

    lastPara.appendChild(link);
  }

  /**
   * Click-to-zoom lightbox for the main gallery image. Loads the full-res
   * (2400px) master stored on each slide's data-zoom-src, so dense panels
   * (nutrition labels especially) stay legible — the inline carousel image is
   * capped at ~1200–1600px for bandwidth, which is soft for fine print.
   *
   * Idempotent: builds the overlay + binds delegated listeners exactly once.
   * Because triggers are delegated on `document`, variant-swap re-renders of
   * the gallery keep working without re-binding.
   *
   * Interaction: click the image or the corner zoom button to open at
   * fit-to-viewport (already far sharper than the inline image). Click the
   * zoomed image to toggle 100% natural size with pan; Esc / backdrop / close
   * button dismiss. Focus is trapped via the theme's global trapFocus helper
   * when available.
   */
  function initZoom() {
    if (window.__pdpZoomInit) return;
    window.__pdpZoomInit = true;

    const overlay = document.createElement('div');
    overlay.className = 'pdp-zoom';
    overlay.hidden = true;
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Zoomed product image');
    overlay.innerHTML =
      '<button class="pdp-zoom__close" type="button" data-pdp-zoom-close aria-label="Close zoom">×</button>' +
      '<div class="pdp-zoom__stage" data-pdp-zoom-stage>' +
        '<img class="pdp-zoom__img" data-pdp-zoom-img alt="">' +
      '</div>';
    document.body.appendChild(overlay);

    const zoomImg = overlay.querySelector('[data-pdp-zoom-img]');
    const stage = overlay.querySelector('[data-pdp-zoom-stage]');
    const closeBtn = overlay.querySelector('[data-pdp-zoom-close]');
    let lastFocused = null;

    function open(src, alt) {
      if (!src) return;
      lastFocused = document.activeElement;
      zoomImg.src = src;
      zoomImg.alt = alt || '';
      overlay.classList.remove('is-zoomed');
      if (stage) stage.scrollTop = stage.scrollLeft = 0;
      overlay.hidden = false;
      document.body.style.overflow = 'hidden';
      if (typeof trapFocus === 'function') {
        trapFocus(overlay, closeBtn);
      } else {
        closeBtn.focus();
      }
    }

    function close() {
      overlay.hidden = true;
      overlay.classList.remove('is-zoomed');
      zoomImg.removeAttribute('src');
      document.body.style.overflow = '';
      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(lastFocused);
      } else if (lastFocused && lastFocused.focus) {
        lastFocused.focus();
      }
    }

    closeBtn.addEventListener('click', close);
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay || e.target === stage) close();
    });
    overlay.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { e.preventDefault(); close(); }
    });
    zoomImg.addEventListener('click', function (e) {
      e.stopPropagation();
      overlay.classList.toggle('is-zoomed');
    });

    function srcFromSlide(slide) {
      return slide ? slide.getAttribute('data-zoom-src') : null;
    }

    document.addEventListener('click', function (e) {
      const imageEl = e.target.closest('.pdp-gallery__image');
      if (imageEl) {
        const slide = imageEl.closest('.pdp-gallery__slide');
        open(srcFromSlide(slide), imageEl.getAttribute('alt'));
        return;
      }
      const zoomBtn = e.target.closest('[data-pdp-gallery-zoom]');
      if (zoomBtn) {
        const main = zoomBtn.closest('[data-pdp-gallery-main]') || zoomBtn.closest('.pdp-hero__gallery');
        const active = main && (main.querySelector('.swiper-slide-active') || main.querySelector('.pdp-gallery__slide'));
        const activeImg = active && active.querySelector('.pdp-gallery__image');
        open(srcFromSlide(active), activeImg && activeImg.getAttribute('alt'));
      }
    });
  }

  function bootstrap() {
    initGalleries();
    initTestimonials();
    initBuyBox();
    initVariantSwitching();
    initDescriptionLink();
    initZoom();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
